import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { useNotification } from "../../../../context/NotificationContext";

const UpdateGuestCountPopup = ({ bookingId, bookingData, isOpenUpdate, onClose, onSubmit }) => {
  const [assignedRooms, setAssignedRooms] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [editingRoom, setEditingRoom] = useState(null); // Track which room is being edited
  const [loading, setLoading] = useState(false);
  const modalRef = useRef(null);
  const submitButtonRef = useRef(null);
  const { notify } = useNotification();

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  useEffect(() => {
    if (isOpenUpdate && bookingId) {
      fetchAssignedRooms();
    }
  }, [isOpenUpdate, bookingId]);

  const initializeForm = (rooms) => {
    const initialValues = {};
    rooms.forEach((room) => {
      initialValues[room.room_number] = {
        num_adults: parseInt(room.num_adults) || 1, // Default to 1 if not provided
        num_children: parseInt(room.num_children) || 0, // Default to 0 if not provided
      };
    });
    setFormValues(initialValues);
  };

  const fetchAssignedRooms = async () => {
    try {
      const response = await axios.get(`${apiUrl}/booking/distinct-assigned-rooms/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setAssignedRooms(response.data.assignedRooms);
        initializeForm(response.data.assignedRooms);
      }
    } catch (error) {
      notify("error","Failed to fetch assigned rooms.");
      console.error("Error fetching assigned rooms:", error);
    }
  };

  const handleEdit = (roomNumber) => {
    setEditingRoom(roomNumber);
  };

  const handleChange = (roomNumber, field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [roomNumber]: {
        ...prev[roomNumber],
        [field]: parseInt(value) || 0, // Ensure value is a number, default to 0 if invalid
      },
    }));
  };

  const handleSaveRoom = (roomNumber) => {
    setEditingRoom(null); // Exit edit mode for this room
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      const payload = {
        bookingId: bookingId,
        roomDetails: assignedRooms.map((room) => {
          const roomData = formValues[room.room_number];
          return {
            room_number: room.room_number,
            num_adults: roomData.num_adults,
            num_children: roomData.num_children,
          };
        }),
      };

      const response = await axios.put(`${apiUrl}/booking/update-pax`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      notify("success","Guest count updated successfully!");
      if (onSubmit) onSubmit(response.data);
      onClose();
      if (submitButtonRef.current) {
        submitButtonRef.current.click();
      }
    } catch (error) {
      notify("error","Failed to update guest count.");
      console.error("Error updating guest count:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
    if (submitButtonRef.current) {
      submitButtonRef.current.click();
    }
  };

  const isCheckedOutOrCancelledOrNoShow = ["checked-out", "cancelled", "noshow"].includes(bookingData?.bookingStatus);
  const disableFields = isCheckedOutOrCancelledOrNoShow;

  return (
    <div ref={modalRef} id="update_guest_count" className="modal custom-modal fade" tabIndex="-1" role="dialog">
      <div
        className="modal-dialog modal-lg"
        style={{ margin: 0, position: "fixed", right: 0, height: "100vh", width: "60%" }}
        role="document"
      >
        <form onSubmit={handleSubmit} style={{ height: "100%" }}>
          <div className="modal-content h-100">
            <div className="modal-header" style={{ backgroundColor: "#f0f8ff", borderBottom: "1px solid #e0e7ff" }}>
              <h5 className="modal-title" style={{ fontWeight: "600", color: "#333" }}>
                Update Guest Count
              </h5>
              <button type="button" className="btn-close" onClick={handleCancel} />
            </div>

            <div className="modal-body" style={{ maxHeight: "calc(100vh - 150px)", overflowY: "auto" }}>
              <div className="row g-2">
                <div className="col-12 mb-2">
                  <div className="card" style={{ border: "1px solid #e0e7ff", padding: "10px" }}>
                    <h6 className="card-title mb-2">Guest Details</h6>
                    <div className="row g-0">
                      <div className="col-6 pe-2">
                        <p><strong>Folio No:</strong> {bookingData?.bookingNumber || "N/A"}</p>
                        <p>
                          <strong>Arrival:</strong>{" "}
                          {new Date(bookingData?.checkIn).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          }) || "N/A"}
                        </p>
                        <p>
                          <strong>Departure:</strong>{" "}
                          {new Date(bookingData?.checkOut).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          }) || "N/A"}
                        </p>
                      </div>
                      <div className="col-6 ps-2">
                        <p><strong>Status:</strong> {bookingData?.bookingStatus || "N/A"}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-12">
                  <div className="card" style={{ border: "1px solid #e0e7ff", padding: "10px" }}>
                    <h6 className="card-title mb-2">Room Details</h6>
                    <div className="table-responsive">
                      <table className="table table-bordered">
                        <thead>
                          <tr style={{ backgroundColor: "#e0e7ff" }}>
                            <th>Room Type</th>
                            <th>Room Number</th>
                            <th>Adults</th>
                            <th>Children</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {assignedRooms.map((room, index) => {
                            const roomKey = room.room_number;
                            const isEditing = editingRoom === roomKey;
                            return (
                              <tr key={index}>
                                <td>{room.room_type}</td>
                                <td>{room.room_number}</td>
                                <td>
                                  {isEditing ? (
                                    <input
                                      type="number"
                                      value={formValues[roomKey]?.num_adults || 1}
                                      onChange={(e) => handleChange(roomKey, "num_adults", e.target.value)}
                                      min="1"
                                      style={{
                                        padding: "4px 8px",
                                        borderRadius: "4px",
                                        border: "1px solid #e0e0e0",
                                        width: "80px",
                                      }}
                                    />
                                  ) : (
                                    formValues[roomKey]?.num_adults || 1
                                  )}
                                </td>
                                <td>
                                  {isEditing ? (
                                    <input
                                      type="number"
                                      value={formValues[roomKey]?.num_children || 0}
                                      onChange={(e) => handleChange(roomKey, "num_children", e.target.value)}
                                      min="0"
                                      style={{
                                        padding: "4px 8px",
                                        borderRadius: "4px",
                                        border: "1px solid #e0e0e0",
                                        width: "80px",
                                      }}
                                    />
                                  ) : (
                                    formValues[roomKey]?.num_children || 0
                                  )}
                                </td>
                                <td>
                                  {isEditing ? (
                                    <button
                                      type="button"
                                      className="btn btn-success btn-sm"
                                      onClick={() => handleSaveRoom(roomKey)}
                                      disabled={disableFields}
                                    >
                                      Save
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      className="btn btn-primary btn-sm"
                                      onClick={() => handleEdit(roomKey)}
                                      disabled={disableFields}
                                    >
                                      Edit
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer" style={{ backgroundColor: "#f0f8ff" }}>
              <button type="submit" className="btn btn-primary" disabled={loading || disableFields}>
                Save
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
              <div style={{ display: "none" }}>
                <button ref={submitButtonRef} data-bs-dismiss="modal" aria-label="Close" type="reset">
                  Submit2
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateGuestCountPopup;